import { EmployeeApi } from 'axiosApi/apis/employee-api';
import { setApiKeyToObject } from 'axiosApi/common';
import { AxiosResponse } from 'axios';

export class HRApiWrapper {
  private employeeApi: EmployeeApi;

  constructor(employeeApi: EmployeeApi) {
    this.employeeApi = employeeApi;
  }

  private async buildHeaders(): Promise<Record<string, string>> {
    const configuration = (this.employeeApi as any).configuration;
    const headers: Record<string, string> = {};
    await setApiKeyToObject(headers, 'Userentity', configuration);
    await setApiKeyToObject(headers, 'Referer', configuration);
    await setApiKeyToObject(headers, 'Authorization', configuration);
    return headers;
  }

  private get axios() { return (this.employeeApi as any).axios; }
  private get basePath(): string { return (this.employeeApi as any).basePath; }

  // ─── Skills ────────────────────────────────────────────────────────────────

  async getSkillCategories(version = '1', options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/Skill/categories`, { headers, ...options });
  }

  async createSkillCategory(version = '1', body: { name: string }, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.post(`${this.basePath}/api/v${version}/Skill/categories`, body, { headers, ...options });
  }

  async updateSkillCategory(version = '1', id: number, body: { name: string }, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.put(`${this.basePath}/api/v${version}/Skill/categories`, { id, ...body }, { headers, ...options });
  }

  async deleteSkillCategory(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.delete(`${this.basePath}/api/v${version}/Skill/categories/${id}`, { headers, ...options });
  }

  async getSkillLevels(version = '1', options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/Skill/levels`, { headers, ...options });
  }

  async createSkillLevel(version = '1', body: { name: string }, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.post(`${this.basePath}/api/v${version}/Skill/levels`, body, { headers, ...options });
  }

  async updateSkillLevel(version = '1', id: number, body: { name: string }, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.put(`${this.basePath}/api/v${version}/Skill/levels`, { id, ...body }, { headers, ...options });
  }

  async deleteSkillLevel(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.delete(`${this.basePath}/api/v${version}/Skill/levels/${id}`, { headers, ...options });
  }

  async getSkills(version = '1', pageNum = 1, pageSize = 100, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/Skill`, { params: { pageNum, pageSize }, headers, ...options });
  }

  async createSkill(version = '1', body: { name: string; idSkillCategory: number; isSoft: boolean }, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.post(`${this.basePath}/api/v${version}/Skill`, body, { headers, ...options });
  }

  async updateSkill(version = '1', id: number, body: { name: string; idSkillCategory: number; isSoft: boolean }, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.put(`${this.basePath}/api/v${version}/Skill`, { id, ...body }, { headers, ...options });
  }

  async deleteSkill(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.delete(`${this.basePath}/api/v${version}/Skill/${id}`, { headers, ...options });
  }

  // ─── Seniority ─────────────────────────────────────────────────────────────

  async getSeniorities(version = '1', options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/Seniority`, { headers, ...options });
  }

  // ─── Education Levels ──────────────────────────────────────────────────────

  async getEducationLevels(version = '1', options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/education-level`, { headers, ...options });
  }

  // ─── Training Certifications ───────────────────────────────────────────────

  async getTrainingCertifications(version = '1', pageNum = 1, pageSize = 20, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/training-certification`, { params: { pageNum, pageSize }, headers, ...options });
  }

  async getTrainingCertificationById(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/training-certification/${id}`, { headers, ...options });
  }

  async createTrainingCertification(version = '1', body: any, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.post(`${this.basePath}/api/v${version}/training-certification`, body, { headers, ...options });
  }

  async updateTrainingCertification(version = '1', id: number, body: any, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.put(`${this.basePath}/api/v${version}/training-certification`, { id, ...body }, { headers, ...options });
  }

  async deleteTrainingCertification(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.delete(`${this.basePath}/api/v${version}/training-certification/${id}`, { headers, ...options });
  }

  async addSkillToTrainingCertification(version = '1', id: number, body: { idSkill: number }, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.post(`${this.basePath}/api/v${version}/training-certification/skill`, { idTrainingCertification: id, ...body }, { headers, ...options });
  }

  async removeSkillFromTrainingCertification(version = '1', id: number, skillId: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.delete(`${this.basePath}/api/v${version}/training-certification/skill/${skillId}`, { headers, ...options });
  }

  // ─── Training Courses ──────────────────────────────────────────────────────

  async getTrainingCourses(version = '1', pageNum = 1, pageSize = 20, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/training-course`, { params: { pageNum, pageSize }, headers, ...options });
  }

  async getTrainingCourseById(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/training-course/${id}`, { headers, ...options });
  }

  async createTrainingCourse(version = '1', body: any, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.post(`${this.basePath}/api/v${version}/training-course`, body, { headers, ...options });
  }

  async updateTrainingCourse(version = '1', id: number, body: any, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.put(`${this.basePath}/api/v${version}/training-course`, { id, ...body }, { headers, ...options });
  }

  async deleteTrainingCourse(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.delete(`${this.basePath}/api/v${version}/training-course/${id}`, { headers, ...options });
  }

  async addSkillToTrainingCourse(version = '1', id: number, body: { idSkill: number }, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.post(`${this.basePath}/api/v${version}/training-course/skill`, { idTrainingCourse: id, ...body }, { headers, ...options });
  }

  async removeSkillFromTrainingCourse(version = '1', id: number, skillId: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.delete(`${this.basePath}/api/v${version}/training-course/skill/${skillId}`, { headers, ...options });
  }

  // ─── Profiles ──────────────────────────────────────────────────────────────

  async getAllProfilesWithSkills(version = '1', options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/Profile/all-with-skills`, { headers, ...options });
  }

  async getProfilesByEmployee(version = '1', employeeId: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/Profile/employee/${employeeId}`, { headers, ...options });
  }

  async getProfileById(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/Profile/${id}`, { headers, ...options });
  }

  async createProfile(version = '1', body: any, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.post(`${this.basePath}/api/v${version}/Profile`, body, { headers, ...options });
  }

  async updateProfile(version = '1', body: any, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.put(`${this.basePath}/api/v${version}/Profile`, body, { headers, ...options });
  }

  async deleteProfile(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.delete(`${this.basePath}/api/v${version}/Profile/${id}`, { headers, ...options });
  }

  async addSkillToProfile(version = '1', body: { idProfile: number; idSkill: number; idSkillLevel: number }, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.post(`${this.basePath}/api/v${version}/Profile/skill`, body, { headers, ...options });
  }

  async updateProfileSkill(version = '1', body: any, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.put(`${this.basePath}/api/v${version}/Profile/skill`, body, { headers, ...options });
  }

  async removeSkillFromProfile(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.delete(`${this.basePath}/api/v${version}/Profile/skill/${id}`, { headers, ...options });
  }

  async assignTrainingCourse(version = '1', body: any, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.post(`${this.basePath}/api/v${version}/Profile/training-course`, body, { headers, ...options });
  }

  async updateTrainingCourseProgress(version = '1', body: any, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.put(`${this.basePath}/api/v${version}/Profile/training-course`, body, { headers, ...options });
  }

  async removeTrainingCourseFromProfile(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.delete(`${this.basePath}/api/v${version}/Profile/training-course/${id}`, { headers, ...options });
  }

  async assignCertification(version = '1', body: any, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.post(`${this.basePath}/api/v${version}/Profile/certification`, body, { headers, ...options });
  }

  async updateCertification(version = '1', body: any, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.put(`${this.basePath}/api/v${version}/Profile/certification`, body, { headers, ...options });
  }

  async removeCertificationFromProfile(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.delete(`${this.basePath}/api/v${version}/Profile/certification/${id}`, { headers, ...options });
  }

  async generateCv(version = '1', profileId: number, idCorporateEntity: number, options?: any): Promise<AxiosResponse<{ data: string }>> {
    const headers = await this.buildHeaders();
    return this.axios.post(`${this.basePath}/api/v${version}/Profile/${profileId}/generate-cv?idCorporateEntity=${idCorporateEntity}`, null, { headers, ...options });
  }

  async uploadCvTemplate(version = '1', corporateEntityId: number, templateFile: File, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    const formData = new FormData();
    formData.append('templateFile', templateFile);
    return this.axios.post(`${this.basePath}/api/v${version}/Profile/cv-template/${corporateEntityId}`, formData, { headers, ...options });
  }

  async getCvTemplateInfo(version = '1', corporateEntityId: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/Profile/cv-template/${corporateEntityId}`, { headers, ...options });
  }

  // ─── Work Experience ───────────────────────────────────────────────────────

  async getWorkExperienceByEmployee(version = '1', employeeId: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/work-experience/by-employee/${employeeId}`, { headers, ...options });
  }

  async createWorkExperience(version = '1', body: any, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.post(`${this.basePath}/api/v${version}/work-experience`, body, { headers, ...options });
  }

  async updateWorkExperience(version = '1', body: any, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.put(`${this.basePath}/api/v${version}/work-experience`, body, { headers, ...options });
  }

  async deleteWorkExperience(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.delete(`${this.basePath}/api/v${version}/work-experience/${id}`, { headers, ...options });
  }

  // ─── Employee Contracts ────────────────────────────────────────────────────

  async getContractsByEmployee(version = '1', employeeId: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/contract/by-employee/${employeeId}`, { headers, ...options });
  }

  async createContract(version = '1', body: any, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.post(`${this.basePath}/api/v${version}/contract`, body, { headers, ...options });
  }

  async updateContract(version = '1', body: any, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.put(`${this.basePath}/api/v${version}/contract`, body, { headers, ...options });
  }

  async deleteContract(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.delete(`${this.basePath}/api/v${version}/contract/${id}`, { headers, ...options });
  }

  async getContractLookups(version = '1', options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/contract/lookups`, { headers, ...options });
  }

  // ─── Job Openings ──────────────────────────────────────────────────────────

  async getJobOpenings(version = '1', pageNum = 1, pageSize = 20, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/job-opening`, { params: { pageNum, pageSize }, headers, ...options });
  }

  async getJobOpeningById(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/job-opening/${id}`, { headers, ...options });
  }

  async createJobOpening(version = '1', body: any, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.post(`${this.basePath}/api/v${version}/job-opening`, body, { headers, ...options });
  }

  async updateJobOpening(version = '1', body: any, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.put(`${this.basePath}/api/v${version}/job-opening`, body, { headers, ...options });
  }

  async deleteJobOpening(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.delete(`${this.basePath}/api/v${version}/job-opening/${id}`, { headers, ...options });
  }

  async addSkillToJobOpening(version = '1', id: number, body: { idSkill: number; required: boolean; skillType: string }, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.post(`${this.basePath}/api/v${version}/job-opening/skill`, { idJobOpening: id, ...body }, { headers, ...options });
  }

  async removeSkillFromJobOpening(version = '1', id: number, skillId: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.delete(`${this.basePath}/api/v${version}/job-opening/skill/${skillId}`, { headers, ...options });
  }

  async getApplicationsByJobOpening(version = '1', jobOpeningId: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/job-application/by-opening/${jobOpeningId}`, { headers, ...options });
  }

  // ─── Job Applications ──────────────────────────────────────────────────────

  async createApplication(version = '1', body: any, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.post(`${this.basePath}/api/v${version}/job-application`, body, { headers, ...options });
  }

  async updateApplicationState(version = '1', id: number, body: { workflowStateId: number }, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.patch(`${this.basePath}/api/v${version}/job-application/state`, { id, ...body }, { headers, ...options });
  }

  async getApplicationResumeUrl(version = '1', applicationId: number, options?: any): Promise<AxiosResponse<{ data: string }>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/job-application/${applicationId}/resume`, { headers, ...options });
  }

  // ─── Job Candidates ────────────────────────────────────────────────────────

  async searchCandidates(version = '1', term = '', pageNum = 1, pageSize = 20, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/JobCandidate/search`, { params: { term, pageNum, pageSize }, headers, ...options });
  }

  async getCandidateById(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/JobCandidate/${id}`, { headers, ...options });
  }

  // ─── Languages ─────────────────────────────────────────────────────────────

  async getLanguages(version = '1', options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/Language`, { headers, ...options });
  }

  async getLanguageLevels(version = '1', options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/Language/levels`, { headers, ...options });
  }

  async createLanguage(version = '1', body: { name: string }, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.post(`${this.basePath}/api/v${version}/Language`, body, { headers, ...options });
  }

  async updateLanguage(version = '1', body: { id: number; name: string }, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.put(`${this.basePath}/api/v${version}/Language`, body, { headers, ...options });
  }

  async deleteLanguage(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.delete(`${this.basePath}/api/v${version}/Language/${id}`, { headers, ...options });
  }

  async addLanguageToProfile(version = '1', body: { idProfile: number; idLanguage: number; idLanguageLevel: number }, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.post(`${this.basePath}/api/v${version}/Profile/language`, body, { headers, ...options });
  }

  async updateProfileLanguage(version = '1', body: { id: number; idLanguageLevel: number }, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json';
    return this.axios.put(`${this.basePath}/api/v${version}/Profile/language`, body, { headers, ...options });
  }

  async removeLanguageFromProfile(version = '1', id: number, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.delete(`${this.basePath}/api/v${version}/Profile/language/${id}`, { headers, ...options });
  }

  // ─── Scope/Functionality matrix ────────────────────────────────────────────

  async getScopeAssignmentsByModule(version = '1', module: string, options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/Functionality/scope-assignments`, { params: { module }, headers, ...options });
  }

  // ─── Employee Picture ───────────────────────────────────────────────────────

  async uploadEmployeePicture(version = '1', employeeId: number, picture: File, options?: any): Promise<AxiosResponse<{ data: string }>> {
    const headers = await this.buildHeaders();
    const formData = new FormData();
    formData.append('picture', picture);
    return this.axios.post(`${this.basePath}/api/v${version}/Employee/${employeeId}/picture`, formData, { headers, ...options });
  }

  async getEmployeePictureUrl(version = '1', employeeId: number, options?: any): Promise<AxiosResponse<{ data: string }>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/Employee/${employeeId}/picture-url`, { headers, ...options });
  }

  async getOrgChartData(version = '1', options?: any): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axios.get(`${this.basePath}/api/v${version}/Employee/org-chart`, { headers, ...options });
  }
}
